import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import throttle from "lodash.throttle";

import { useIoCContext } from "@context/IoCContext/IoCContext";
import { Types } from "@ioc/types";
import { IAuthService } from "@modules/user/models/IAuthService";
import { IBlockUserService } from "@modules/user/models/IBlockUserService";
import { IListUsersService } from "@modules/user/models/IListUsersService";
import { IUnlockUserService } from "@modules/user/models/IUnlockUserService";
import AppError from "@utils/AppError";
import { IUser } from "@utils/interfaces";
import { calcPagesQuantity } from "@utils/pagination";
import { useFlags } from "flagsmith/react";

const ROWS_PER_PAGE = 10;

export function useListUsers() {
  const { enqueueSnackbar } = useSnackbar();
  const iocContext = useIoCContext();

  const listUsersService = iocContext.serviceContainer.get<IListUsersService>(
    Types.Users.IListUsersService
  );
  const blockUserService = iocContext.serviceContainer.get<IBlockUserService>(
    Types.Users.IBlockUserService
  );
  const unlockUserService = iocContext.serviceContainer.get<IUnlockUserService>(
    Types.Users.IUnlockUserService
  );
  const authService = iocContext.serviceContainer.get<IAuthService>(
    Types.Users.IAuthService
  );

  const flag = useFlags(["is_carrier_type_user"]);
  const isCarrierEnabled = flag.is_carrier_type_user.enabled;

  const [users, setUsers] = useState<IUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchUsers, setSearchUsers] = useState("");
  const [page, setPage] = useState(0);
  const [active, setActive] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  const fetchUsers = useCallback(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingUsers(true);
        const data = await listUsersService.execute(searchUsers);
        if (mounted) setUsers(data);
      } catch {
        if (mounted)
          enqueueSnackbar("Ocorreu um erro ao recuperar usuários", {
            variant: "error",
          });
      } finally {
        if (mounted) setLoadingUsers(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [enqueueSnackbar, listUsersService, searchUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, active]);

  const displayedUsers = useMemo(() => {
    const filtered = users.filter((u) => u.enabled === active);
    if (!isCarrierEnabled) return filtered.filter((u) => u.kind !== "carrier");
    return filtered;
  }, [users, active, isCarrierEnabled]);

  const paginatedUsers = useMemo(
    () =>
      displayedUsers.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE),
    [displayedUsers, page]
  );

  const totalPages = calcPagesQuantity(ROWS_PER_PAGE, displayedUsers.length);

  const handleBlockUser = useCallback(
    async (userID: string) => {
      try {
        const result = await blockUserService.execute(userID);
        enqueueSnackbar(result.message, { variant: "success" });
      } catch (error) {
        const msg =
          error instanceof AppError
            ? error.message
            : "Ocorreu um erro ao bloquear usuário";
        enqueueSnackbar(msg, {
          variant: error instanceof AppError ? error.variant : "error",
        });
      } finally {
        setShowBlockModal(false);
        fetchUsers();
      }
    },
    [blockUserService, enqueueSnackbar, fetchUsers]
  );

  const handleUnlockUser = useCallback(
    async (userID: string) => {
      try {
        const result = await unlockUserService.execute(userID);
        enqueueSnackbar(result.message, { variant: "success" });
      } catch (error) {
        const msg =
          error instanceof AppError
            ? error.message
            : "Ocorreu um erro ao desbloquear usuário";
        enqueueSnackbar(msg, {
          variant: error instanceof AppError ? error.variant : "error",
        });
      } finally {
        setShowBlockModal(false);
        fetchUsers();
      }
    },
    [unlockUserService, enqueueSnackbar, fetchUsers]
  );

  const handleResetPassword = async (email: string) => {
    if (!email) return;
    try {
      setResetLoading(true);
      await authService.forgotPassword(email);
      enqueueSnackbar(
        `Instruções de redefinição de senha enviadas para ${email}`,
        { variant: "success", autoHideDuration: 6000 }
      );
    } catch (e) {
      enqueueSnackbar(
        e instanceof AppError
          ? e.message
          : "Ocorreu um erro ao tentar redefinir a senha.",
        { variant: "error" }
      );
    } finally {
      setResetLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledSearch = useCallback(
    throttle((v: string) => setSearchUsers(v), 1000),
    []
  );

  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
    setActive(newValue === 0);
    setPage(0);
  };

  return {
    loadingUsers,
    paginatedUsers,
    displayedUsers,
    totalPages,
    page,
    setPage,
    active,
    tabIndex,
    handleTabChange,
    showBlockModal,
    setShowBlockModal,
    selectedUserId,
    setSelectedUserId,
    showDetailsModal,
    setShowDetailsModal,
    selectedUser,
    setSelectedUser,
    handleBlockUser,
    handleUnlockUser,
    handleResetPassword,
    resetLoading,
    throttledSearch,
    fetchUsers,
  };
}
